import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Surface, Text, useTheme } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { usePhoneVerification } from '@/src/hooks/usePhoneVerification';

const formatCountdown = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60)
    .toString()
    .padStart(2, '0');
  const remainder = (safe % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

export default function PhoneVerifyViaBotScreen() {
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{
    code?: string;
    expires_in?: string;
    whatsapp_number?: string;
    associated?: string;
  }>();

  const {
    botCode,
    botCountdown,
    botStatus,
    error,
    startBotFlow,
    markBotAssociated,
  } = usePhoneVerification();

  useEffect(() => {
    const queryCode = params.code || '123456';
    const expiresIn = Number(params.expires_in || '600');
    startBotFlow(queryCode, Number.isFinite(expiresIn) ? expiresIn : 600);
  }, [params.code, params.expires_in, startBotFlow]);

  useEffect(() => {
    if (params.associated === '1') {
      markBotAssociated();
    }
  }, [params.associated, markBotAssociated]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Surface style={styles.content} elevation={1}>
        <Text variant="headlineSmall" style={styles.title}>
          Verificar número via bot WhatsApp
        </Text>

        <Text variant="bodyMedium" style={styles.description}>
          Envie este código no WhatsApp do suporte para associar sua conta.
        </Text>

        <View style={[styles.codeBox, { borderColor: theme.colors.primary }]}> 
          <Text style={[styles.codeText, { color: theme.colors.primary }]}>{botCode || '------'}</Text>
        </View>

        <Text variant="bodyLarge" style={styles.countdownText}>
          Expira em: {formatCountdown(botCountdown)}
        </Text>

        {params.whatsapp_number ? (
          <Text variant="bodySmall" style={styles.metaText}>
            Número WhatsApp: {params.whatsapp_number}
          </Text>
        ) : null}

        {botStatus === 'associated' ? (
          <Text style={[styles.statusText, { color: theme.colors.primary }]}>Conta associada com sucesso ✅</Text>
        ) : (
          <Text style={styles.statusText}>Estado: aguardando confirmação no bot</Text>
        )}

        {!!error && <Text style={{ color: theme.colors.error }}>{error}</Text>}

        {botStatus !== 'associated' ? (
          <Button mode="contained" onPress={markBotAssociated} style={styles.button}>
            Já confirmei no bot
          </Button>
        ) : null}

        <Button mode="text" onPress={() => router.replace('/dashboard/user-data')}>
          Voltar ao perfil
        </Button>
      </Surface>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: {
    margin: 16,
    padding: 20,
    borderRadius: 12,
    gap: 12,
  },
  title: {
    textAlign: 'center',
    fontWeight: '600',
  },
  description: {
    textAlign: 'center',
    opacity: 0.8,
  },
  codeBox: {
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 88,
  },
  codeText: {
    textAlign: 'center',
    fontSize: 44,
    lineHeight: 50,
    letterSpacing: 4,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
    includeFontPadding: false,
  },
  countdownText: {
    textAlign: 'center',
    fontWeight: '600',
  },
  metaText: {
    textAlign: 'center',
    opacity: 0.8,
  },
  statusText: {
    textAlign: 'center',
  },
  button: {
    marginTop: 6,
  },
});
